import React, { useEffect, useState } from 'react';
import { TSystemsMark } from '@/components/brand/TSystemsMark';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import FormBuilder, { FormBuilderState } from '@/components/form-builder/FormBuilder';
import api from '@/lib/api';

const FormBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formId: id } = useParams<{ formId?: string }>();
  const { t } = useTranslation('common');
  const tt = (key: string, fallback: string) => t(key, { defaultValue: fallback });

  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<Partial<FormBuilderState>>(
    (location.state as Partial<FormBuilderState>) || {}
  );

  useEffect(() => {
    if (id) {
      loadFormTemplate(id);
    }
  }, [id]);

  const loadFormTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/flow-engine/form-templates/${templateId}`);

      if (response.data.success && response.data.formTemplate) {
        const template = response.data.formTemplate;
        setInitialData({
          formName: template.formName,
          formTitle: template.formTitle,
          formDescription: template.formDescription,
          // campos vindos da API (jsonb) não têm o `id` interno do builder;
          // sem ele o key do React e a seleção/edição quebram
          fields: (template.fields ?? []).map((field: any) => ({
            ...field,
            id: field.id ?? field.fieldId,
          })),
          submitButtonText: template.submitButtonText,
          successMessage: template.successMessage,
          redirectUrl: template.redirectUrl,
          theme: template.theme,
          config: template.config,
        });
      }
    } catch (error: any) {
      console.error('Error loading form template:', error);
      toast.error(tt('formBuilder.toasts.loadError', 'Error loading form template'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data: FormBuilderState) => {
    try {
      setLoading(true);

      const slug = (value: string) =>
        (value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 255);

      const name = slug(data.formName) || slug(data.formTitle) || 'formulario';

      const payload = {
        name,
        description: data.formDescription,
        formName: name,
        formTitle: data.formTitle,
        formDescription: data.formDescription,
        fields: data.fields.map((field) => ({
          ...field,
          fieldName: field.fieldName || field.fieldId,
        })),
        submitButtonText: data.submitButtonText,
        successMessage: data.successMessage,
        redirectUrl: data.redirectUrl,
        theme: data.theme,
        config: data.config,
      };

      let response;
      if (id) {
        response = await api.patch(`/flow-engine/form-templates/${id}`, payload);
      } else {
        response = await api.post('/flow-engine/form-templates', payload);
      }

      if (response.data.success) {
        toast.success(
          id
            ? tt('formBuilder.toasts.updated', 'Form updated successfully')
            : tt('formBuilder.toasts.created', 'Form created successfully')
        );
        navigate('/forms');
      }
    } catch (error: any) {
      console.error('Error saving form template:', error);
      toast.error(error.response?.data?.message || tt('formBuilder.toasts.saveError', 'Error saving form'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/forms');
  };

  if (loading && id && !initialData?.formTitle) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-brand-600" />
          <p className="text-slate-600 dark:text-gray-400">{tt('formBuilder.loading', 'Loading form...')}</p>
        </div>
      </div>
    );
  }

  return <FormBuilder formId={id} initialData={initialData} onSave={handleSave} onCancel={handleCancel} />;
};

export default FormBuilderPage;
